import { validateNotContainsEmail } from '@melio/ap-domain';
import { useVendors } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useConfig } from '@melio/platform-provider';
import { object, SchemaOf, string } from 'yup';

import { AddVendorFormFields } from '../types';

export const useCompanyNameSchema = ({
  showCompanyField,
  isManaged,
}: {
  showCompanyField?: boolean;
  isManaged: boolean;
}) => {
  const { formatMessage } = useMelioIntl();
  const {
    settings: {
      vendor: {
        createVendor: { canAddVendorWithTheSameName, companyNameFormatRegexp, companyNameMaxLength },
      },
    },
  } = useConfig();
  const [vendorNameConstraintsEnabled] = useFeature(FeatureFlags.PlatformVendorNameConstraints, false);
  const fetchVendors = showCompanyField && !canAddVendorWithTheSameName;
  const { data: vendors } = useVendors({ enabled: fetchVendors });

  if (!showCompanyField) {
    // TODO: do the validation and display error in a banner if the field is not displayed https://meliorisk.atlassian.net/browse/ME-33938
    return object().shape({ companyName: string() }) as SchemaOf<Pick<AddVendorFormFields, 'companyName'>>;
  }

  return object().shape({
    companyName: string()
      .required(formatMessage('widgets.addVendorForm.companyName.validation.required'))
      .when({
        is: () => companyNameMaxLength,
        then: (schema) =>
          schema.max(
            companyNameMaxLength as number,
            formatMessage('widgets.addVendorForm.companyName.validation.maxLength')
          ),
      })
      .when({
        is: () => companyNameFormatRegexp,
        then: (schema) =>
          schema.matches(
            companyNameFormatRegexp as RegExp,
            formatMessage('widgets.addVendorForm.companyName.validation.format')
          ),
      })
      .when({
        is: () => !canAddVendorWithTheSameName,
        then: (schema) =>
          schema
            .required(formatMessage('widgets.addVendorForm.companyName.validation.required'))
            .test('current-vendors-loaded', '', () => !!vendors)
            .test(
              'is-company-name-not-exists',
              formatMessage('widgets.addVendorForm.companyName.validation.alreadyExists'),
              (companyName) => !vendors?.some((vendor) => vendor.name === companyName)
            ),
      })
      .test(
        'not-contains-email',
        formatMessage('form.vendorSelect.constraints.email'),
        validateNotContainsEmail({ isEnabled: vendorNameConstraintsEnabled, isManaged })
      ),
  }) as SchemaOf<Pick<AddVendorFormFields, 'companyName'>>;
};

export const useAccountNumberSchema = () => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    accountNumber: string()
      .required(formatMessage('widgets.addVendorForm.accountNumber.validation.required'))
      .max(32, formatMessage('widgets.addVendorForm.accountNumber.validation.maxLength'))
      .matches(/^[!"#$%&\-0-9A-Za-z]*$/, formatMessage('widgets.addVendorForm.accountNumber.validation.format')),
    confirmAccountNumber: string()
      .required(formatMessage('widgets.addVendorForm.confirmAccountNumber.validation.required'))
      .test(
        'match-account-number',
        formatMessage('widgets.addVendorForm.confirmAccountNumber.validation.match'),
        (confirmAccountNumber, context) =>
          confirmAccountNumber === (context.parent as AddVendorFormFields).accountNumber
      ),
  }) as SchemaOf<Pick<AddVendorFormFields, 'accountNumber' | 'confirmAccountNumber'>>;
};

export const useNicknameSchema = () => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    nickname: string()
      .nullable()
      .max(30, formatMessage('widgets.addVendorForm.nickname.validation.maxLength'))
      .matches(/^[ ,.\-0-9A-Za-z\r\n]*$/, formatMessage('widgets.addVendorForm.nickname.validation.format')),
  }) as SchemaOf<Pick<AddVendorFormFields, 'nickname'>>;
};

const ADDRESS_FIELDS_FORMAT_REGEXP = /^[ 0-9A-Za-z_@!+#.$,/%^*-]*$/;
const ADDRESS_FIELDS_MAX_LENGTH = 32;

export const useAddressLine1Schema = () => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    line1: string()
      .required(formatMessage('widgets.addVendorForm.addressLine1.validation.required'))
      .max(ADDRESS_FIELDS_MAX_LENGTH, formatMessage('widgets.addVendorForm.addressLine1.validation.maxLength'))
      .matches(ADDRESS_FIELDS_FORMAT_REGEXP, formatMessage('widgets.addVendorForm.addressLine1.validation.format')),
  }) as SchemaOf<Pick<AddVendorFormFields, 'line1'>>;
};

export const useAddressLine2Schema = () => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    line2: string()
      .nullable()
      .max(ADDRESS_FIELDS_MAX_LENGTH, formatMessage('widgets.addVendorForm.addressLine2.validation.maxLength'))
      .matches(ADDRESS_FIELDS_FORMAT_REGEXP, formatMessage('widgets.addVendorForm.addressLine2.validation.format')),
  }) as SchemaOf<Pick<AddVendorFormFields, 'line2'>>;
};

export const useCitySchema = () => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    city: string()
      .required(formatMessage('widgets.addVendorForm.city.validation.required'))
      .max(ADDRESS_FIELDS_MAX_LENGTH, formatMessage('widgets.addVendorForm.city.validation.maxLength'))
      .matches(ADDRESS_FIELDS_FORMAT_REGEXP, formatMessage('widgets.addVendorForm.city.validation.format')),
  }) as SchemaOf<Pick<AddVendorFormFields, 'city'>>;
};

export const useZipCodeSchema = (isZipCodeNeeded = true) => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    ...(isZipCodeNeeded
      ? {
          postalCode: string()
            .required(formatMessage('widgets.addVendorForm.postalCode.validation.required'))
            .matches(/^(\d{5}|\d{9}|\d{11})$/, formatMessage('widgets.addVendorForm.postalCode.validation.format')),
        }
      : {}),
  }) as SchemaOf<Pick<AddVendorFormFields, 'postalCode'>>;
};
